import {ModalComponentProps, Card, Block, Dialog, Col, Row, DialogBody, DialogFooter, Button, VF, Space, Text, Yup, makeValidate} from '@mc/ui-v4';

import {Form} from 'react-final-form';
import React, {useMemo} from 'react';
import {EnumActionType, ActionConfigVO, AttributeConfigVO, ActionItemWrapperVO} from '../../../api';
import TreeActions from '../../treeActions/TreeActions';
import {NodeModel} from '@minoru/react-dnd-treeview';

type Props = ModalComponentProps & {
    initialValues: ActionConfigVO;
    diskriminatorAtribut?: AttributeConfigVO;
    isEdit: boolean;
    onSubmit: (data: ActionConfigVO) => void;
    atributy: AttributeConfigVO[];
};

const validationSchema = (diskriminatorAtributId?: number, initialTyp?: EnumActionType) =>
    Yup.object().shape({
        nazev: Yup.string().required('Je třeba zadat název akce'),
        typ: Yup.mixed<EnumActionType>().oneOf(Object.values(EnumActionType)).required('Je třeba zadat typ akce'),
        polozky: Yup.array()
            .when('typ', {
                is: (typ: EnumActionType) => typ !== EnumActionType.DELETE,
                then: Yup.array().required('Akce musí obsahovat skupinu'),
            })
            .test('hasParent', 'Skupina musí obsahovat atributy', polozky => {
                if (initialTyp === EnumActionType.DELETE) return true;
                if (!Array.isArray(polozky) || polozky.length === 0) return false;
                const hasGroup = polozky.some(p => p?.parent === 0);
                const hasAttribute = polozky.some(p => p?.droppable === false);
                return hasGroup && hasAttribute;
            })
            .test('hasDiscriminator', 'Musí obsahovat atribut s diskriminátorem', function (polozky) {
                if (!diskriminatorAtributId || initialTyp === EnumActionType.DELETE) return true; // Pokud diskriminatorAtributId neexistuje, není potřeba kontrolovat
                if (!Array.isArray(polozky)) return false;
                return polozky.some(polozka => polozka?.id === diskriminatorAtributId);
            }),
    });

export const UpravaAkceModal: React.FC<Props> = ({initialValues, diskriminatorAtribut, isEdit, onSubmit, onClose, atributy}) => {
    const validate = useMemo(() => makeValidate(validationSchema(diskriminatorAtribut?.id, initialValues.typ)), [diskriminatorAtribut?.id]);
    const getAtributTree = (polozky: ActionItemWrapperVO[]): NodeModel[] => {
        let resultList: NodeModel[] = [];
        let skupinyHash = {};
        polozky.forEach(polozka => {
            //přidání atributů do struktury stromu
            if (polozka.akceAtribut) {
                const atribut = atributy.find(item => item.id === polozka.akceAtribut.vrstvaAtributId);
                resultList.push({
                    id: polozka.akceAtribut.vrstvaAtributId,
                    parent: polozka.skupina?.id,
                    droppable: false,
                    text: atribut?.popisek ?? '<neznámý atribut>',
                });
            }

            //přidání skupin do hash
            if (polozka.skupina && !skupinyHash[polozka.skupina.id]) {
                skupinyHash[polozka.skupina.id] = {
                    id: polozka.skupina.id,
                    parent: 0,
                    droppable: true,
                    text: polozka.skupina.nazev,
                };
            }
        });

        //naplnění stromu skupinama
        Object.values(skupinyHash).forEach((skupina: NodeModel) => {
            resultList.push(skupina);
        });

        return resultList;
    };
    const getPolozkySubmit = (polozky: NodeModel[]): ActionItemWrapperVO[] => {
        let tranforemedPolozky = [];
        let skupinyHash = {};

        //zpracuj skupiny
        polozky
            .filter(item => item.parent === 0)
            .forEach((skupina, index) => {
                if (!skupinyHash[skupina.id]) {
                    //pokud skupina.id ze stromu obsahuje prefix "tmp-" tak se jedná o novou položku a id se serveru neposílá
                    skupinyHash[skupina.id] = {
                        nazev: skupina.text,
                        poradi: index,
                        id: typeof skupina.id === 'string' && skupina.id.startsWith('temp-') ? undefined : skupina.id,
                    };
                }
            });

        //zpracuj polozky atributu
        let orderInGroup = 0;
        polozky
            .filter(item => item.parent !== 0)
            .forEach(polozka => {
                orderInGroup = orderInGroup + 1;
                //protože jsou v jednom poli všechny položky všech skupin, musíme si jejich pořadí ukládat separátně
                // let order = 0;
                // if (orderInGroup[polozka.parent]) {
                //     order = orderInGroup[polozka.parent] + 1;
                //     orderInGroup[polozka.parent] = order;
                // } else {
                //     orderInGroup[polozka.parent] = order;
                // }

                //přidej hotovou položku
                tranforemedPolozky.push({
                    skupina: skupinyHash[polozka.parent],
                    akceAtribut: {vrstvaAtributId: polozka.id, poradi: orderInGroup},
                });
            });
        return tranforemedPolozky;
    };

    return (
        <Form<ActionConfigVO>
            initialValues={initialValues}
            onSubmit={async data => {
                //položky nejsou ve správném formátu (mají interní strukturu knihovního stromu)
                //musíme před submitem přetransformovat
                const {polozky, ...other} = data;
                const tranforemedPolozky = data.typ === EnumActionType.DELETE ? null : getPolozkySubmit(polozky as NodeModel[]);
                const transformedData: ActionConfigVO = {...other, polozky: tranforemedPolozky};
                onSubmit(transformedData);
            }}
            validate={async data => {
                return await validate(data);
            }}
        >
            {({handleSubmit, submitting, values, errors, submitFailed, form: {change}}) => {
                // Odfiltrujeme z výběru diskriminační atribut
                const filteredAtributy = atributy.filter(item => item.id !== diskriminatorAtribut?.id);
                const treeData = getAtributTree(values.polozky ? values.polozky : []);
                const externalData: NodeModel[] = filteredAtributy
                    .filter(atribut => !treeData.some(treeItem => treeItem.id === atribut.id))
                    .filter(atribut => atribut.zobrazit) // zobrazit pouze atributy, které jsou viditelné
                    .map(item => {
                        return {
                            id: item.id,
                            parent: 0,
                            droppable: false,
                            text: item.popisek,
                        };
                    });

                return (
                    <Dialog size={'lg'} onClose={onClose} title={isEdit ? 'Úprava akce' : 'Nová akce'} showOnTop>
                        <DialogBody>
                            <Space vertical>
                                <Row>
                                    <Col>
                                        <Card mode={'edit'}>
                                            <Block>
                                                <VF.String disabled={submitting} name={'nazev'} label={'Název akce'} />
                                                <VF.RadioGroup
                                                    name={'typ'}
                                                    label={'Typ akce'}
                                                    horizontal
                                                    disabled={submitting || values.typ === EnumActionType.DELETE}
                                                >
                                                    <VF.Radio
                                                        label="Vytvoření objektu"
                                                        name="typ"
                                                        value={EnumActionType.INSERT}
                                                        checked={EnumActionType.INSERT === values?.typ}
                                                        disabled
                                                    />
                                                    <VF.Radio
                                                        label="Změna objektu"
                                                        name="typ"
                                                        value={EnumActionType.UPDATE}
                                                        checked={EnumActionType.UPDATE === values?.typ}
                                                        disabled
                                                        onChange={e => {
                                                            change(e.target.name, e.target.value);
                                                            change('vychozi', false);
                                                        }}
                                                    />
                                                    <VF.Radio
                                                        label="Odstranění objektu"
                                                        name="typ"
                                                        value={EnumActionType.DELETE}
                                                        checked={EnumActionType.DELETE === values?.typ}
                                                        disabled
                                                        onChange={e => {
                                                            change(e.target.name, e.target.value);
                                                        }}
                                                    />
                                                </VF.RadioGroup>
                                                {values.typ === EnumActionType.INSERT && (
                                                    <VF.RadioGroup name={'vychozi'} label={'Výchozí akce'} horizontal={false}>
                                                        <VF.Checkbox
                                                            key={`default-${initialValues?.id}`}
                                                            disabled={submitting}
                                                            type="checkbox"
                                                            name={'vychozi'}
                                                        />
                                                    </VF.RadioGroup>
                                                )}
                                            </Block>
                                        </Card>
                                    </Col>
                                </Row>
                                {values.typ !== EnumActionType.DELETE && ( // pro delete nenastavujeme atributy
                                    <TreeActions data={treeData} externalDataInit={externalData} diskriminatorAtribut={diskriminatorAtribut} />
                                )}
                                {
                                    // @ts-ignore kvůli polozka.parent
                                    submitFailed && errors.polozky && <Text intent="danger">{errors.polozky}</Text>
                                }
                            </Space>
                        </DialogBody>
                        <DialogFooter>
                            <Button disabled={submitting} style={'link'} onClick={onClose}>
                                Zrušit
                            </Button>
                            <Button style={'primary'} disabled={submitting} type="submit" onClick={handleSubmit}>
                                {isEdit ? 'Uložit' : 'Vytvořit'}
                            </Button>
                        </DialogFooter>
                    </Dialog>
                );
            }}
        </Form>
    );
};
